import React, { useState, useEffect } from 'react';
import { CarService, ICarModel, ICreateCarRequest } from '../../services/car.service';
import { formatCarNumber } from '../../utils/carNumberUtils';
import './addCarModal.css';

interface AddCarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (carData: ICreateCarRequest) => void;
  autoparkID: string;
}

const carTypes = [
  { value: 'Седан', label: 'Седан' },
  { value: 'Хэтчбек', label: 'Хэтчбек' },
  { value: 'Универсал', label: 'Универсал' },
  { value: 'Купе', label: 'Купе' },
  { value: 'Кабриолет', label: 'Кабриолет' },
  { value: 'Внедорожник', label: 'Внедорожник' },
  { value: 'Кроссовер', label: 'Кроссовер' },
  { value: 'Пикап', label: 'Пикап' },
  { value: 'Минивэн', label: 'Минивэн' },
  { value: 'Фургон', label: 'Фургон' },
];

function AddCarModal({ isOpen, onClose, onConfirm, autoparkID }: AddCarModalProps) {
  const [carData, setCarData] = useState<ICreateCarRequest>({
    carType: '',
    mark: '',
    model: '',
    number: '',
    autoparkID,
  });

  const [carMarks, setCarMarks] = useState<string[]>([]);
  const [carModels, setCarModels] = useState<ICarModel[]>([]);
  const [loadingMarks, setLoadingMarks] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Получение марок автомобилей
  const fetchCarMarks = async () => {
    setLoadingMarks(true);
    try {
      const data = await CarService.getCarMarks();
      setCarMarks(data);
    } catch (error) {
      console.error('Ошибка при запросе марок:', error);
      setCarMarks([]);
    } finally {
      setLoadingMarks(false);
    }
  };

  // Получение моделей при изменении марки
  const fetchCarModels = async (mark: string) => {
    if (!mark.trim()) {
      setCarModels([]);
      return;
    }

    setLoadingModels(true);
    try {
      const data = await CarService.getCarModels(mark);
      setCarModels(data);
    } catch (error) {
      console.error('Ошибка при запросе моделей:', error);
      setCarModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  // Загружаем марки при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      fetchCarMarks();
      setCarData({
        carType: '',
        mark: '',
        model: '',
        number: '',
        autoparkID,
      });
      setErrors({});
      setCarModels([]);
      // Блокируем скролл при открытии модального окна
      document.body.style.overflow = 'hidden';
    } else {
      // Разблокируем скролл при закрытии
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Обновляем список моделей при изменении марки
  useEffect(() => {
    if (carData.mark) {
      fetchCarModels(carData.mark);
    }
  }, [carData.mark]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleChange = (field: keyof ICreateCarRequest, value: string) => {
    setCarData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Очищаем ошибку для данного поля
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }

    // Если изменяется марка, очищаем модель
    if (field === 'mark') {
      setCarData((prev) => ({
        ...prev,
        model: '',
      }));
      setCarModels([]);
    }
  };

  const handleCarNumberChange = (value: string) => {
    const formattedNumber = formatCarNumber(value);
    handleChange('number', formattedNumber);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!carData.carType) {
      newErrors.carType = 'Выберите тип кузова';
    }

    if (!carData.mark) {
      newErrors.mark = 'Выберите марку';
    }

    if (!carData.model) {
      newErrors.model = 'Выберите модель';
    }

    if (!carData.number) {
      newErrors.number = 'Введите номер автомобиля';
    } else if (carData.number.length < 8) {
      newErrors.number = 'Номер автомобиля должен быть корректным';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onConfirm(carData);
      onClose();
    } catch (error) {
      console.error('Ошибка при создании автомобиля:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-car-modal-overlay" onClick={handleBackdropClick}>
      {/* Кнопка закрытия */}
      <button className="add-car-modal-close-btn" onClick={onClose}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <g clipPath="url(#clip0_354_1355)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M11.9993 13.4141L17.6563 19.0711C17.8449 19.2533 18.0975 19.3541 18.3597 19.3518C18.6219 19.3495 18.8727 19.2444 19.0582 19.059C19.2436 18.8736 19.3487 18.6227 19.351 18.3606C19.3533 18.0984 19.2525 17.8458 19.0703 17.6571L13.4133 12.0001L19.0703 6.34315C19.2525 6.15455 19.3533 5.90194 19.351 5.63975C19.3487 5.37755 19.2436 5.12674 19.0582 4.94133C18.8727 4.75592 18.6219 4.65075 18.3597 4.64848C18.0975 4.6462 17.8449 4.74699 17.6563 4.92915L11.9993 10.5861L6.34234 4.92915C6.15288 4.75149 5.90175 4.65451 5.64207 4.65873C5.38238 4.66295 5.13453 4.76803 4.95094 4.95174C4.76736 5.13546 4.66245 5.38339 4.65842 5.64307C4.65439 5.90276 4.75155 6.15382 4.92933 6.34315L10.5853 12.0001L4.92833 17.6571C4.83282 17.7494 4.75664 17.8597 4.70423 17.9817C4.65182 18.1037 4.62424 18.235 4.62308 18.3677C4.62193 18.5005 4.64723 18.6322 4.69751 18.7551C4.74779 18.878 4.82205 18.9897 4.91594 19.0835C5.00983 19.1774 5.12148 19.2517 5.24438 19.302C5.36728 19.3523 5.49896 19.3776 5.63174 19.3764C5.76452 19.3752 5.89574 19.3477 6.01774 19.2953C6.13974 19.2428 6.25009 19.1667 6.34234 19.0711L11.9993 13.4141Z"
              fill="#3563E9"
            />
          </g>
          <defs>
            <clipPath id="clip0_354_1355">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </button>

      <div className="add-car-modal-wrapper">
        <div className="add-car-modal">
          {/* Заголовок */}
          <h2 className="add-car-modal-title">Добавить автомобиль</h2>

          {/* Форма */}
          <div className="add-car-form">
            {/* Тип кузова */}
            <div className="form-group">
              <label htmlFor="carType">Тип кузова</label>
              <select
                id="carType"
                value={carData.carType}
                onChange={(e) => handleChange('carType', e.target.value)}
                className={`form-select ${errors.carType ? 'error' : ''}`}
                onKeyDown={handleKeyDown}
              >
                <option value="">Выберите тип кузова</option>
                {carTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.carType && <div className="error-message">{errors.carType}</div>}
            </div>

            {/* Марка */}
            <div className="form-group">
              <label htmlFor="mark">Марка</label>
              <select
                id="mark"
                value={carData.mark}
                onChange={(e) => handleChange('mark', e.target.value)}
                className={`form-select ${errors.mark ? 'error' : ''}`}
                disabled={loadingMarks}
                onKeyDown={handleKeyDown}
              >
                <option value="">Выберите марку</option>
                {carMarks.map((mark) => (
                  <option key={mark} value={mark}>
                    {mark}
                  </option>
                ))}
              </select>
              {loadingMarks && <div className="loading-text">Загрузка марок...</div>}
              {errors.mark && <div className="error-message">{errors.mark}</div>}
            </div>

            {/* Модель */}
            <div className="form-group">
              <label htmlFor="model">Модель</label>
              <select
                id="model"
                value={carData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                className={`form-select ${errors.model ? 'error' : ''}`}
                disabled={!carData.mark || loadingModels}
                onKeyDown={handleKeyDown}
              >
                <option value="">Выберите модель</option>
                {carModels.map((model) => (
                  <option key={`${model.mark}-${model.model}`} value={model.model}>
                    {model.model}
                  </option>
                ))}
              </select>
              {loadingModels && <div className="loading-text">Загрузка моделей...</div>}
              {errors.model && <div className="error-message">{errors.model}</div>}
            </div>

            {/* Номер автомобиля */}
            <div className="form-group">
              <label htmlFor="number">Номер автомобиля</label>
              <input
                type="text"
                id="number"
                value={carData.number}
                onChange={(e) => handleCarNumberChange(e.target.value)}
                className={`autopark-form-input ${errors.number ? 'error' : ''}`}
                placeholder="А 111 АА 111"
                maxLength={12}
                onKeyDown={handleKeyDown}
              />
              {errors.number && <div className="error-message">{errors.number}</div>}
            </div>
          </div>

          {/* Кнопки */}
          <div className="add-car-modal-actions">
            <button className="add-car-confirm-btn" onClick={handleConfirm} disabled={loading}>
              {loading ? 'Создание...' : 'Добавить'}
            </button>
            <button className="add-car-cancel-btn" onClick={onClose} disabled={loading}>
              Отменить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddCarModal;
